import React, { useState, useEffect, useRef } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { HiMenuAlt3, HiX, HiChevronDown, HiDocumentText, HiGlobeAlt, HiCode, HiPencilAlt, HiPaperAirplane, HiLightBulb } from 'react-icons/hi';
import servicesData from '../data/servicesData';

const serviceIcons = {
  'cv-writing': HiDocumentText,
  'visa-processing': HiGlobeAlt,
  'software-collaboration': HiCode,
  'content-writing': HiPencilAlt,
  'flight-tickets': HiPaperAirplane,
  'small-business-support': HiLightBulb,
};

const navLinks = [
  { to: 'home', label: 'Home' },
  { to: 'about', label: 'About' },
  { to: 'services', label: 'Services', hasDropdown: true },
  { to: 'why-me', label: 'Why Me' },
  { to: 'testimonials', label: 'Testimonials' },
  { to: 'contact', label: 'Contact' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setDropdownOpen(false);
    setMobileServicesOpen(false);
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setDropdownOpen(false), 200);
  };

  const NavLink = ({ to, children, className, onClick }) => {
    if (isHomePage) {
      return (
        <ScrollLink
          to={to}
          spy={true}
          smooth={true}
          offset={-80}
          duration={500}
          activeClass="text-primary font-semibold"
          className={className}
          onClick={onClick}
        >
          {children}
        </ScrollLink>
      );
    }
    return (
      <RouterLink
        to={`/#${to}`}
        className={className}
        onClick={onClick}
      >
        {children}
      </RouterLink>
    );
  };

  return (
    <>
      {/* Mobile Menu Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled || !isHomePage
            ? 'bg-white/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-16 md:h-20">
          {isHomePage ? (
            <ScrollLink
              to="home"
              smooth={true}
              duration={500}
              className="cursor-pointer"
            >
              <span className="font-display text-xl md:text-2xl font-bold text-primary-dark">
                Marina<span className="text-primary-light"></span>
              </span>
            </ScrollLink>
          ) : (
            <RouterLink to="/" className="cursor-pointer">
              <span className="font-display text-xl md:text-2xl font-bold text-primary-dark">
                Marina<span className="text-primary-light"></span>
              </span>
            </RouterLink>
          )}

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div
                  key={link.to}
                  className="relative"
                  ref={dropdownRef}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <NavLink
                    to={link.to}
                    className="inline-flex items-center gap-1 text-text-light hover:text-primary transition-colors duration-300 cursor-pointer text-sm font-medium tracking-wide"
                  >
                    {link.label}
                    <HiChevronDown
                      className={`text-xs transition-transform duration-300 ${
                        dropdownOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </NavLink>

                  {/* Dropdown */}
                  <div
                    className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-300 ${
                      dropdownOpen
                        ? 'opacity-100 visible translate-y-0'
                        : 'opacity-0 invisible -translate-y-2'
                    }`}
                  >
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-3 w-72">
                      {/* Arrow */}
                      <div className="absolute -top-[6px] left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-gray-100 rotate-45 mt-4" />

                      <div className="relative z-10">
                        {/* All Services Link */}
                        <NavLink
                          to="services"
                          className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-soft-pink/40 transition-all duration-200 cursor-pointer group mb-1"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all duration-200">
                            <HiDocumentText className="text-primary group-hover:text-white text-lg transition-colors duration-200" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-text-dark group-hover:text-primary transition-colors">
                              All Services
                            </p>
                            <p className="text-xs text-text-light">
                              View all my services
                            </p>
                          </div>
                        </NavLink>

                        <div className="h-px bg-gray-100 mx-3 my-1" />

                        {/* Individual Services */}
                        {servicesData.map((service) => {
                          const Icon = serviceIcons[service.slug] || service.icon;
                          return (
                            <RouterLink
                              key={service.slug}
                              to={`/services/${service.slug}`}
                              className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-soft-pink/40 transition-all duration-200 group"
                              onClick={() => setDropdownOpen(false)}
                            >
                              <div className={`w-9 h-9 rounded-lg ${service.iconBg} flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}>
                                <Icon className={`${service.iconColor} text-lg`} />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-text-dark group-hover:text-primary transition-colors">
                                  {service.title}
                                </p>
                                <p className="text-xs text-text-light line-clamp-1">
                                  {service.tagline}
                                </p>
                              </div>
                            </RouterLink>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className="text-text-light hover:text-primary transition-colors duration-300 cursor-pointer text-sm font-medium tracking-wide"
                >
                  {link.label}
                </NavLink>
              )
            )}
            <NavLink
              to="contact"
              className="bg-primary text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-primary-dark transition-colors duration-300 cursor-pointer"
            >
              Get in Touch
            </NavLink>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-primary-dark text-2xl"
            aria-label="Toggle menu"
          >
            {isOpen ? <HiX /> : <HiMenuAlt3 />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        } bg-white/95 backdrop-blur-md shadow-lg`}
      >
        <div className="px-6 py-4 space-y-1">
          {navLinks.map((link) =>
            link.hasDropdown ? (
              <div key={link.to}>
                {/* Services Toggle */}
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="flex items-center justify-between w-full text-text-light hover:text-primary transition-colors duration-300 text-sm font-medium py-2"
                >
                  {link.label}
                  <HiChevronDown
                    className={`text-xs transition-transform duration-300 ${
                      mobileServicesOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Mobile Dropdown */}
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    mobileServicesOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pl-4 pb-2 space-y-1">
                    <NavLink
                      to="services"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 py-2 text-text-light hover:text-primary transition-colors duration-300 cursor-pointer text-sm"
                    >
                      <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center">
                        <HiDocumentText className="text-primary text-sm" />
                      </div>
                      All Services
                    </NavLink>

                    {servicesData.map((service) => {
                      const Icon = serviceIcons[service.slug] || service.icon;
                      return (
                        <RouterLink
                          key={service.slug}
                          to={`/services/${service.slug}`}
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-3 py-2 text-text-light hover:text-primary transition-colors duration-300 text-sm"
                        >
                          <div className={`w-7 h-7 rounded-md ${service.iconBg} flex items-center justify-center`}>
                            <Icon className={`${service.iconColor} text-sm`} />
                          </div>
                          {service.title}
                        </RouterLink>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setIsOpen(false)}
                className="block text-text-light hover:text-primary transition-colors duration-300 cursor-pointer text-sm font-medium py-2"
              >
                {link.label}
              </NavLink>
            )
          )}
          <NavLink
            to="contact"
            onClick={() => setIsOpen(false)}
            className="block bg-primary text-white px-5 py-2 rounded-full text-sm font-medium text-center hover:bg-primary-dark transition-colors duration-300 cursor-pointer mt-2"
          >
            Get in Touch
          </NavLink>
        </div>
      </div>
    </nav>
    </>
  );
};

export default Navbar;
